import { types, flow } from 'mobx-state-tree'
import makeInspectable from 'mobx-devtools-mst'
import cloneDeep from 'lodash/cloneDeep'

import { IProjectModel, Project } from '@linqpal/models/src/mst/Project'
import { editableModel } from '@linqpal/models'
import { CustomerAccount, Invoice, routes2 } from '@linqpal/models'
import { crudSideBarStore } from '../../../store/ScreensStore/Supplier/crudSideBarStoreGenerator'

const textMatchThreshold = 0.1

enum ParamKeys {
  Page = 'page',
  PageSize = 'pageSize',
}

type ListingData = {
  result: string
  items: IProjectModel[]
  count: number
}

export const invoiceExtendModelType = types.array(
  Invoice.props({
    _id: types.string,
    isChecked: types.optional(types.boolean, false),
    customer: types.maybeNull(CustomerAccount.props({ _id: types.string })),
  }),
)

const props = {
  isVisibleSidebar: types.optional(types.boolean, false),
  listing: types.optional(types.array(Project), []),
  [ParamKeys.Page]: types.optional(types.number, 1),
  [ParamKeys.PageSize]: types.optional(types.number, 10),
  loading: types.optional(types.boolean, true),
  loadingDelete: false,
  count: 0,
  chosenName: '',
  chosenAddress: '',
  chosenAmount: 0,
  chosenNotes: '',
  receivables: invoiceExtendModelType,
  payables: invoiceExtendModelType,
  income: invoiceExtendModelType,
  expenses: invoiceExtendModelType,
  stats: types.model({
    receivables: types.optional(types.union(types.string, types.number), '0'),
    income: types.optional(types.union(types.string, types.number), '0'),
    expense: types.optional(types.union(types.string, types.number), '0'),
  }),
  invoiceListing: invoiceExtendModelType,
  viewInvoice: types.optional(types.boolean, false),
  invoiceId: types.maybeNull(types.union(types.string, types.number)),
  selectedInvoices: types.array(types.string),
  noteIsTypingNow: types.optional(types.boolean, false),
  projectDetail: Project,
  type: types.string,
  activeInvoice: types.frozen(),
  refresher: types.optional(types.number, 0),
}

function prepareName(customer) {
  if (!customer) return ''
  let name = ''
  if (customer.first_name) {
    name = customer.first_name
  }
  if (customer.last_name) {
    name = name ? name + ' ' + customer.last_name : customer.last_name
  }
  return name ? name.trim() : ''
}

const actions = (self: any) => ({
  resetRefresher: () => (self.refresher += 1),
  updateType: (type: any) => (self.type = type),
  updateProjectDetail: (data) => {
    self.projectDetail = {
      _id: data?._id,
      name: data?.name,
      company_id: data?.company_id,
      projectId: data?._id,
      totalInvoices: data?.totalInvoices,
      lastActivityDate: data?.lastActivityDate,
      address: data?.address,
      notes: data?.notes,
      contractValue: data?.contractValue,
      updatedAt: data?.updatedAt,
      customer_account_id: data?.customer_account_id,
      customerName: data?.customerName,
      data: data?.data,
    }

    self.projectDetail.invoices = cloneDeep(data?.invoices)

    self.projectDetail.customer = cloneDeep({
      ...data?.customer,
      name: prepareName(data?.customer),
    })
  },
  updateReceivables: (data) => {
    self.receivables.replace(data)
    self.receivables?.customer?.replace(data?.customer)
  },
  updateIncome: (data) => {
    self.income.replace(data)
    self.income.customer.replace(data?.customer)
  },
  updatePayables: (data) => {
    self.payables.replace(data)
    self.payables.customer.replace(data?.customer)
  },
  updateExpenses: (data) => {
    self.expenses.replace(data)
    self.expenses.customer.replace(data?.customer)
  },
  toggleSideBar: () => (self.isVisibleSidebar = !self.isVisibleSidebar),
  setNoteIsTypingNow: (bool: boolean) => (self.noteIsTypingNow = bool),
  setViewInvoice: (bool: boolean) => (self.viewInvoice = bool),
  resetEverything: () => {
    self.stats = {
      expense: '0',
      income: '0',
      receivables: '0',
    }
    self.invoiceListing = []
    self.receivables = []
    self.income = []
    self.expenses = []
    self.payables = []

    self.selectedInvoices = []

    self.projectDetail = {}

    self.type = 'receivable'
  },
  setActiveInvoice: (invoice: any) => (self.activeInvoice = invoice),
  updateSelectedInvoices: ({ selectedRows }) =>
    (self.selectedInvoices = selectedRows?.map(
      (item: { _id: string; id: string }) => item?._id || item?.id,
    )),
  updateProjectStats: (data) => (self.stats = data),
  updateInvoiceListing: (data) => (self.invoiceListing = data),
  updateListing: (data: { items: IProjectModel; count: number }) => {
    self.listing = data.items
    self.count = data.count
  },
  setLoading: (bool) => {
    self.loading = bool
  },
  setLoadingDelete: (bool) => {
    self.loadingDelete = bool
  },
  setPageNum: (pageNumber: number) => {
    self[ParamKeys.Page] = pageNumber
    self.fetchListing()
  },
  setPageSize: (pageSize: number) => {
    self[ParamKeys.PageSize] = pageSize
    self.fetchListing()
  },
  onSearchListing: flow(function* (search: string) {
    const response: ListingData = yield routes2.project.findAll({
      page: self.page,
      pageSize: self.pageSize,
      search,
    })
    if ((response.result = 'ok')) {
      if (response?.items?.length > 0) {
        self.updateListing(response)
      }
      self.setLoading(false)
    }
  }),
  fetchListing: flow(function* () {
    const response: ListingData = yield routes2.project.findAll({
      page: self.page,
      pageSize: self.pageSize,
    })
    if ((response.result = 'ok')) {
      if (response?.items?.length > 0) {
        self.updateListing(response)
      }
      self.setLoading(false)
    }
  }),
  deleteProject: flow(function* (id, cb) {
    const deletedResult = yield routes2.project.del(id)
    if ((deletedResult.result = 'ok')) {
      cb()
    }
  }),
  updateProject: flow(function* (id, item = {}) {
    if (item.hasOwnProperty('name')) {
      const result = yield routes2.project.update(id, item)
      return result
    }
    const result = routes2.project.update(id, {
      notes: self.chosenNotes,
      data: {},
    })
    return result
  }),
  getProjectTabInvoices: flow(function* (params) {
    const response = yield routes2.project.getInvoices({ ...params })

    if ((response.result = 'ok')) {
      const convertedInvoices = response.items.map((item) => {
        const material_subtotal = item.material_subtotal?.toString()
        const refunded_amount = item.refunded_amount?.toString()
        const tax_amount = item.tax_amount?.toString()
        const id = item?._id

        return {
          ...item,
          id,
          tax_amount,
          refunded_amount,
          material_subtotal,
        }
      })

      if (params?.category === 'receivable' && params?.status === '') {
        self.updateReceivables(convertedInvoices)
      }

      if (params?.category === 'receivable' && params?.status === 'PAID') {
        self.updateIncome(convertedInvoices)
      }

      if (params?.category === 'payable' && params?.status === '') {
        self.updatePayables(convertedInvoices)
      }

      if (params?.category === 'payable' && params?.status === 'PAID') {
        self.updateExpenses(convertedInvoices)
      }
    }
  }),
  getProjectStats: flow(function* (id) {
    const response = yield routes2.project.getStatistics(id)

    if ((response.result = 'ok')) {
      self.updateProjectStats(response.data)
    }
  }),

  addInvoicesToProject: flow(function* (projectId, cb) {
    const response = yield routes2.project.addInvoices(projectId, {
      invoices: self.selectedInvoices,
    })

    if ((response.result = 'ok')) {
      self.updateSelectedInvoices([])
      cb()
    }
  }),
  removeInvoicesFromProject: flow(function* (projectId, cb) {
    const response = yield routes2.project.removeInvoices(projectId, {
      invoices: self.selectedInvoices,
    })

    if ((response.result = 'ok')) {
      self.updateSelectedInvoices([])
      cb()
    }
  }),

  getAddInvoiceListing: flow(function* (params) {
    const response = yield routes2.invoice.findAll({ ...params })

    if ((response.result = 'ok')) {
      const convertedInvoices = response.items.map((item) => {
        const material_subtotal = item.material_subtotal?.toString()
        const tax_amount = item.tax_amount?.toString()
        const refunded_amount = item.refunded_amount?.toString()

        return {
          ...item,
          material_subtotal,
          tax_amount,
          refunded_amount,
        }
      })

      self.updateInvoiceListing(convertedInvoices)
    }
  }),

  getProjectDetail: flow(function* (id) {
    const response = yield routes2.project.findById(id)

    if ((response.result = 'ok')) {
      self.updateProjectDetail(response.data)
    }
  }),
  isProjectMissingOnAllInvoices(invoices) {
    return invoices.every((inv) => !inv.project_id)
  },
  areInvoicesFromSameProject(invoices) {
    const projectId = invoices[0].project_id
    return invoices.every((i) => i.project_id && i.project_id === projectId)
  },
  isSearchScoreMoreThanThreshold(score) {
    return score >= textMatchThreshold
  },
})

export const ProjectsStoreModel = types
  .compose(
    types.model(props).actions(actions),
    editableModel(),
    crudSideBarStore(Project),
  )
  .named('ProjectsStore')

const projectsStore = ProjectsStoreModel.create({
  listing: [],
  chosenDetails: {
    isOpenedSidebar: false,
  },

  receivables: [],
  payables: [],
  income: [],
  expenses: [],

  stats: {
    receivables: '0',
    income: '0',
    expense: '0',
  },
  invoiceListing: [],
  projectDetail: {},
  type: 'receivable',
  refresher: 0,
})

makeInspectable(projectsStore)

export default projectsStore
